import React from "react";
import { Link } from "react-router-dom";
import { fileUrl, youtubeId } from "@/lib/api";

/*
  Tiny, safe formatter for blog posts (no raw HTML is ever injected).
  Supported:  # / ## / ### headings · **bold** · *italic* · [link](https://…) · ![image](url)
              - bullet / 1. numbered lists · > quote · blank line = new paragraph · --- divider
              a YouTube link on its own line  → video player
              several images on consecutive lines → photo gallery grid
*/

const IMG_LINE = /^!\[([^\]]*)\]\(([^)\s]+)\)$/;
const YT_LINE = /^(?:@\[youtube\]\()?(https?:\/\/(?:www\.|m\.)?(?:youtube\.com|youtube-nocookie\.com|youtu\.be)\/\S+?)\)?$/i;
const imgSrc = (u) => (u.startsWith("/") || /^https?:/i.test(u) ? fileUrl(u) : null);

const safeHref = (url) => {
  const u = (url || "").trim();
  if (/^(https?:|mailto:|tel:)/i.test(u) || u.startsWith("/") || u.startsWith("#")) return u;
  return null;
};

function inline(text, keyPrefix = "i") {
  const out = [];
  const re = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0; let m; let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={`${keyPrefix}${k++}`} className="font-semibold text-neutral-900">{m[2]}</strong>);
    else if (m[3]) out.push(<em key={`${keyPrefix}${k++}`}>{m[4]}</em>);
    else if (m[5]) {
      const href = safeHref(m[7]);
      if (!href) out.push(m[6]);
      else if (href.startsWith("/")) out.push(<Link key={`${keyPrefix}${k++}`} to={href} className="text-[#0E4D34] underline underline-offset-2">{m[6]}</Link>);
      else out.push(<a key={`${keyPrefix}${k++}`} href={href} target="_blank" rel="noreferrer" className="text-[#0E4D34] underline underline-offset-2">{m[6]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className = "" }) {
  const lines = (text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0; let key = 0;
  const para = [];
  const flush = () => {
    if (para.length) { blocks.push(<p key={key++} className="mb-5 leading-relaxed">{inline(para.join(" "), `p${key}`)}</p>); para.length = 0; }
  };
  while (i < lines.length) {
    const line = lines[i]; const t = line.trim();
    if (!t) { flush(); i++; continue; }
    let m;
    if ((m = t.match(/^(#{1,3})\s+(.*)$/))) {
      flush();
      const lvl = m[1].length;
      const cls = lvl === 1 ? "text-3xl mt-10 mb-4" : lvl === 2 ? "text-2xl mt-9 mb-3" : "text-xl mt-7 mb-2";
      blocks.push(React.createElement(lvl === 1 ? "h2" : lvl === 2 ? "h3" : "h4", { key: key++, className: `font-semibold text-neutral-900 ${cls}` }, inline(m[2], `h${key}`)));
      i++; continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(t)) { flush(); blocks.push(<hr key={key++} className="my-8 border-neutral-200"/>); i++; continue; }
    if ((m = t.match(YT_LINE)) && youtubeId(m[1])) {
      flush();
      const id = youtubeId(m[1]);
      blocks.push(
        <div key={key++} className="my-8 aspect-video rounded-2xl overflow-hidden bg-neutral-900 shadow-lg" data-testid="blog-youtube">
          <iframe title="YouTube video" src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                  className="w-full h-full border-0" loading="lazy"/>
        </div>);
      i++; continue;
    }
    if (IMG_LINE.test(t)) {
      flush();
      const imgs = [];
      while (i < lines.length && (m = lines[i].trim().match(IMG_LINE))) {
        const src = imgSrc(m[2]);
        if (src) imgs.push({ src, alt: m[1] });
        i++;
      }
      if (imgs.length === 1) {
        blocks.push(
          <figure key={key++} className="my-7">
            <img src={imgs[0].src} alt={imgs[0].alt} className="w-full rounded-2xl" loading="lazy"/>
            {imgs[0].alt && <figcaption className="text-sm text-neutral-500 mt-2 text-center">{imgs[0].alt}</figcaption>}
          </figure>);
      } else if (imgs.length > 1) {
        blocks.push(
          <div key={key++} className={`my-8 grid gap-3 grid-cols-2 ${imgs.length % 3 === 0 ? "sm:grid-cols-3" : ""}`} data-testid="blog-gallery">
            {imgs.map((im, j) => (
              <figure key={j} className="relative rounded-2xl overflow-hidden group">
                <img src={im.src} alt={im.alt} className="w-full aspect-[4/5] object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy"/>
                {im.alt && <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent text-white text-xs px-3 pt-6 pb-2">{im.alt}</figcaption>}
              </figure>
            ))}
          </div>);
      }
      continue;
    }
    if (/^>\s?/.test(t)) {
      flush(); const q = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) { q.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      blocks.push(<blockquote key={key++} className="my-6 border-l-4 border-[#D4AF37] pl-5 italic text-neutral-700">{inline(q.join(" "), `q${key}`)}</blockquote>);
      continue;
    }
    if (/^[-*]\s+/.test(t) || /^\d+[.)]\s+/.test(t)) {
      flush();
      const ordered = /^\d+[.)]\s+/.test(t); const items = [];
      while (i < lines.length && (ordered ? /^\d+[.)]\s+/ : /^[-*]\s+/).test(lines[i].trim())) {
        items.push(lines[i].trim().replace(ordered ? /^\d+[.)]\s+/ : /^[-*]\s+/, "")); i++;
      }
      const Tag = ordered ? "ol" : "ul";
      blocks.push(<Tag key={key++} className={`mb-5 pl-6 space-y-1.5 ${ordered ? "list-decimal" : "list-disc"} marker:text-[#0E4D34]`}>{items.map((it, j) => <li key={j}>{inline(it, `l${key}-${j}`)}</li>)}</Tag>);
      continue;
    }
    para.push(t); i++;
  }
  flush();
  return <div className={`text-[17px] text-neutral-700 ${className}`}>{blocks}</div>;
}
